/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import type { CoreAppStore, uiTypes } from '@opengeoweb/store';
import {
  getSingularDrawtoolDrawLayerId,
  layerActions,
  layerSelectors,
  layerUtils,
  uiActions,
  uiSelectors,
  useSetupDialog,
} from '@opengeoweb/store';
import { LayerType } from '@opengeoweb/webmap';
import { emptyGeoJSON } from '@opengeoweb/webmap-react';
import { AreaDrawingManager } from './AreaDrawingManager';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { useShareDrawing } from './shareDrawing';
import {
  useDrawingDetails,
  useDrawings,
  useDrawingsPredefinedAreas,
} from '../../api/warning-api/hooks';
import { warningsDrawingsActions } from '../../store/warningsDrawings/reducer';
import { selectDrawByInstanceId } from '../../store/warningsDrawings/selectors';
import type {
  AreaListItem,
  DrawingListItem,
} from '../../store/warningsDrawings/types';
import {
  drawingDialogType,
  areaDrawingDialogType,
  getDrawingFeatureId,
} from '../../store/warningsDrawings/utils';
import { useWarningsTranslation } from '../../utils/i18n';
import { useDrawingDrawDialogAction } from '../DrawingTool/useDrawingDialogAction';

export const AREA_FILL_OPACITY = 0.0;
export const DRAWING_FILL_OPACITY = 0.1;
export const HOVER_HIGHLIGHT_FILL_OPACITY = 0.2;

const buildAreaGeoJSON = (
  drawing: DrawingListItem | AreaListItem,
  options: {
    fillOpacity: number;
    idPrefix?: string;
    highlightedFeatureIndex?: number;
  } = { fillOpacity: AREA_FILL_OPACITY },
): GeoJSON.FeatureCollection => ({
  ...drawing.geoJSON,
  features: drawing.geoJSON.features.map((feature, index) => ({
    ...feature,
    id:
      feature.id ??
      `${options.idPrefix ?? ''}${getDrawingFeatureId(drawing.id, feature, index)}`,
    properties: {
      ...feature.properties,
      areaId: drawing.id,
      areaFeatureName:
        feature.properties?.objectName ??
        feature.properties?.name ??
        feature.id,
      isReadonly: true,
      mapFeatureClass: 'default',
      isSelected: false,
      fill: '#186DFF',
      'fill-opacity':
        index === options.highlightedFeatureIndex
          ? HOVER_HIGHLIGHT_FILL_OPACITY
          : options.fillOpacity,
      stroke: '#006CFD',
    },
  })),
});

export const AreaDrawingManagerConnect: React.FC<{
  bounds?: string;
  source?: uiTypes.Source;
}> = ({ bounds, source = 'app' }) => {
  const { isLoggedIn } = useAuthenticationContext();
  const { t } = useWarningsTranslation();
  const { dialogOrder, setDialogOrder, isDialogOpen, uiSource, uiError } =
    useSetupDialog(areaDrawingDialogType, source);
  const [selectedDrawingId, setSelectedDrawingId] = useState<
    string | undefined
  >();

  const [selectedTab, setSelectedTab] = React.useState(1);

  const {
    data: drawingDetails,
    isFetching: isFetchingDetails,
    error: drawingError,
  } = useDrawingDetails(selectedDrawingId!);

  const dispatch = useDispatch();
  const shareDrawing = useShareDrawing();
  const mapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, areaDrawingDialogType),
  );
  const { closeAreaDrawingDialog, openDrawDialog } = useDrawingDrawDialogAction(
    {
      mapId,
      source,
    },
  );
  const geoJSONLayerId = getSingularDrawtoolDrawLayerId(mapId);
  const hoverPreviewLayerId = `area-hover-preview-${mapId}`;

  const currentLayer = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerByIdWithoutTimeDimension(store, geoJSONLayerId),
  );
  const currrentDrawing = useSelector((store: CoreAppStore) =>
    selectDrawByInstanceId(store, drawingDialogType),
  );
  const activeDrawingId = currrentDrawing?.id || '';
  const selectedAreaFeatureNamesByAreaId = React.useMemo(() => {
    const selectedFeatureIdSet = new Set(
      currentLayer?.selectedFeatureIds ?? [],
    );
    const selectedIndices =
      currentLayer?.geojson?.features.reduce<number[]>(
        (indices, feature, index) => {
          const featureKey = layerUtils.getFeatureSelectionKey(feature);
          if (featureKey && selectedFeatureIdSet.has(featureKey)) {
            indices.push(index);
          }
          return indices;
        },
        [],
      ) ?? [];

    if (selectedIndices.length === 0) {
      return {};
    }

    const result: Record<string, string[]> = {};

    selectedIndices.forEach((selectedFeatureIndex) => {
      if (selectedFeatureIndex === undefined) {
        return;
      }

      const selectedFeature =
        currentLayer?.geojson?.features[selectedFeatureIndex];
      const areaId = selectedFeature?.properties?.areaId;
      if (!areaId || typeof areaId !== 'string') {
        return;
      }

      const selectedFeatureName =
        selectedFeature.properties?.objectName ??
        selectedFeature.properties?.name ??
        selectedFeature.id ??
        selectedFeatureIndex;

      if (!result[areaId]) {
        result[areaId] = [];
      }
      result[areaId].push(String(selectedFeatureName));
    });

    return result;
  }, [currentLayer]);

  const {
    data: drawings = [],
    error,
    isFetching: isFetchingList,
  } = useDrawings({}, isDialogOpen);

  const {
    data: predefinedAreas = [],
    error: predefinedAreasError,
    isFetching: isFetchingPredefinedAreasList,
  } = useDrawingsPredefinedAreas(isDialogOpen);

  useEffect(() => {
    if (error || predefinedAreasError || !isLoggedIn) {
      const currentError = error || predefinedAreasError;
      const message = currentError?.message ?? t('error-auth-login');
      dispatch(
        uiActions.setErrorDialog({
          error: message,
          type: areaDrawingDialogType,
        }),
      );
    }
  }, [error, predefinedAreasError, isLoggedIn, dispatch, t]);

  const deleteGeoJSONLayer = useCallback((): void => {
    if (geoJSONLayerId && currentLayer) {
      dispatch(
        layerActions.layerChangeGeojson({
          layerId: geoJSONLayerId,
          geojson: emptyGeoJSON,
        }),
      );
      dispatch(
        layerActions.toggleFeatureMode({
          layerId: geoJSONLayerId,
          isInEditMode: false,
        }),
      );
    }
  }, [geoJSONLayerId, currentLayer, dispatch]);

  const showGeoJSONLayer = useCallback(
    (drawing: DrawingListItem | AreaListItem, isArea = false): void => {
      const geoJSON = buildAreaGeoJSON(drawing, {
        fillOpacity: isArea ? AREA_FILL_OPACITY : DRAWING_FILL_OPACITY,
      });

      if (!currentLayer) {
        const layerPayload = {
          geojson: geoJSON,
          isSelectable: isArea,
          isInEditMode: false,
          enabled: true,
        };
        dispatch(
          layerActions.addLayer({
            mapId,
            layer: layerPayload,
            layerId: geoJSONLayerId,
            origin: 'warnings-lib',
          }),
        );
      } else {
        dispatch(
          layerActions.layerChangeGeojson({
            layerId: geoJSONLayerId,
            geojson: geoJSON,
          }),
        );
        dispatch(
          layerActions.toggleFeatureMode({
            layerId: geoJSONLayerId,
            isInEditMode: false,
          }),
        );
        dispatch(
          layerActions.setSelectedFeatures({
            layerId: geoJSONLayerId,
            selectedFeatureIds: [],
          }),
        );
        if (currentLayer.isSelectable !== isArea) {
          dispatch(
            layerActions.toggleSelectableFeatures({ layerId: geoJSONLayerId }),
          );
        }
      }
    },
    [currentLayer, dispatch, mapId, geoJSONLayerId],
  );

  const onClickDrawing = async (drawing: DrawingListItem): Promise<void> => {
    const isActive = drawing.id === currrentDrawing?.id;

    dispatch(
      warningsDrawingsActions.setDrawValues({
        objectName: drawing.objectName,
        drawingInstanceId: drawingDialogType,
        id: !isActive ? drawing.id : '',
      }),
    );

    if (isActive) {
      deleteGeoJSONLayer();
    } else {
      showGeoJSONLayer(drawing);
    }
  };

  const onClickArea = useCallback(
    (drawing: AreaListItem): void => {
      const isActive = drawing.id === currrentDrawing?.id;

      dispatch(
        warningsDrawingsActions.setDrawValues({
          objectName: drawing.objectName,
          drawingInstanceId: drawingDialogType,
          id: !isActive ? drawing.id : '',
        }),
      );

      if (isActive) {
        deleteGeoJSONLayer();
      } else {
        showGeoJSONLayer(drawing, true);
      }
    },
    [currrentDrawing?.id, dispatch, deleteGeoJSONLayer, showGeoJSONLayer],
  );

  const hoverPreviewLayer = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerByIdWithoutTimeDimension(store, hoverPreviewLayerId),
  );
  const hasHoverPreviewLayerRef = useRef(Boolean(hoverPreviewLayer));

  useEffect(() => {
    hasHoverPreviewLayerRef.current = Boolean(hoverPreviewLayer);
  }, [hoverPreviewLayer]);

  const deleteHoverPreviewLayer = useCallback((): void => {
    hasHoverPreviewLayerRef.current = false;
    dispatch(
      layerActions.layerDelete({
        layerId: hoverPreviewLayerId,
        mapId,
        layerIndex: 0,
      }),
    );
  }, [dispatch, hoverPreviewLayerId, mapId]);

  useEffect(() => {
    return (): void => {
      deleteHoverPreviewLayer();
    };
  }, [deleteHoverPreviewLayer]);

  const showHoverPreviewLayer = useCallback(
    (
      drawing: DrawingListItem | AreaListItem,
      highlightedFeatureIndex?: number,
    ): void => {
      const hoverGeoJSON = buildAreaGeoJSON(drawing, {
        fillOpacity: AREA_FILL_OPACITY,
        idPrefix: 'hover-',
        highlightedFeatureIndex,
      });

      if (!hasHoverPreviewLayerRef.current) {
        hasHoverPreviewLayerRef.current = true;
        dispatch(
          layerActions.addLayer({
            mapId,
            layer: {
              geojson: hoverGeoJSON,
              layerType: LayerType.featureLayer,
              isSelectable: false,
              isInEditMode: false,
              enabled: true,
            },
            layerId: hoverPreviewLayerId,
            origin: 'warnings-lib',
          }),
        );
      } else {
        dispatch(
          layerActions.layerChangeGeojson({
            layerId: hoverPreviewLayerId,
            geojson: hoverGeoJSON,
          }),
        );
      }
    },
    [dispatch, mapId, hoverPreviewLayerId],
  );

  const onHover = useCallback(
    (drawing: DrawingListItem | AreaListItem): void => {
      if (drawing.id === currrentDrawing?.id) {
        return;
      }

      showHoverPreviewLayer(drawing);
    },
    [currrentDrawing?.id, showHoverPreviewLayer],
  );

  const onHoverEnd = useCallback((): void => {
    deleteHoverPreviewLayer();
  }, [deleteHoverPreviewLayer]);

  const highlightDrawingFeature = useCallback(
    (drawing: DrawingListItem, highlightedFeatureIndex?: number): void => {
      if (drawing.id === currrentDrawing?.id) {
        dispatch(
          layerActions.layerChangeGeojson({
            layerId: geoJSONLayerId,
            geojson: buildAreaGeoJSON(drawing, {
              fillOpacity: DRAWING_FILL_OPACITY,
              highlightedFeatureIndex,
            }),
          }),
        );
        return;
      }

      showHoverPreviewLayer(drawing, highlightedFeatureIndex);
    },
    [currrentDrawing?.id, dispatch, geoJSONLayerId, showHoverPreviewLayer],
  );

  const onHoverDrawingFeature = useCallback(
    (drawing: DrawingListItem, featureIndex: number): void => {
      highlightDrawingFeature(drawing, featureIndex);
    },
    [highlightDrawingFeature],
  );

  const onHoverDrawingFeatureEnd = useCallback(
    (drawing: DrawingListItem): void => {
      highlightDrawingFeature(drawing, undefined);
    },
    [highlightDrawingFeature],
  );

  const [confirmDialogOptions, setConfirmDialogOptions] = React.useState<
    | {
        drawingId: string;
        drawingName: string;
      }
    | undefined
  >(undefined);

  const openDrawTools = (drawingId: string): void => {
    dispatch(
      uiActions.setErrorDialog({
        error: '',
        type: areaDrawingDialogType,
      }),
    );

    deleteHoverPreviewLayer();

    if (drawingId !== activeDrawingId) {
      deleteGeoJSONLayer();
    }

    setSelectedDrawingId(drawingId);
  };

  useEffect(() => {
    if (
      !selectedDrawingId ||
      !drawingDetails ||
      drawingDetails?.id !== selectedDrawingId ||
      isFetchingDetails
    ) {
      return;
    }

    dispatch(
      warningsDrawingsActions.setDrawValues({
        objectName: drawingDetails.objectName,
        id: selectedDrawingId,
        drawingInstanceId: drawingDialogType,
      }),
    );

    openDrawDialog(
      drawingDetails.transformation?.original ?? drawingDetails.geoJSON,
    );
    setSelectedDrawingId(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDrawingId, drawingDetails, dispatch, isFetchingDetails]);

  useEffect(() => {
    if (drawingError) {
      dispatch(
        uiActions.setErrorDialog({
          error: drawingError.message,
          type: areaDrawingDialogType,
        }),
      );
    }
  }, [dispatch, drawingError]);

  useEffect(() => {
    const selectedFeatureIndex = layerUtils.getSelectedFeatureIndexByIds(
      currentLayer?.geojson,
      currentLayer?.selectedFeatureIds,
    );

    if (selectedFeatureIndex === undefined) {
      return;
    }

    const selectedFeature =
      currentLayer?.geojson?.features[selectedFeatureIndex];

    if (!selectedFeature) {
      return;
    }

    const areaId = selectedFeature.properties?.areaId;
    if (!areaId) {
      return;
    }

    const area = predefinedAreas.find(
      (predefinedArea) => predefinedArea.id === areaId,
    );

    if (area && area.id !== currrentDrawing?.id) {
      onClickArea(area);
    }
  }, [currentLayer, currrentDrawing, onClickArea, predefinedAreas]);

  const onEditDrawing = (drawingId: string): void => {
    void openDrawTools(drawingId);
  };

  const onClose = (): void => {
    deleteHoverPreviewLayer();
    closeAreaDrawingDialog();
  };

  const onCreateNewDrawing = (): void => {
    closeAreaDrawingDialog();
    openDrawDialog();
  };

  const onDeleteSuccess = (drawingId: string): void => {
    // close confirm dialog
    setConfirmDialogOptions(undefined);
    // remove any geoJSON layer of deleted drawing if set
    if (activeDrawingId && activeDrawingId === drawingId) {
      deleteGeoJSONLayer();
    }
  };

  const onDeleteDrawing = (drawingId: string): void => {
    const currentDrawing = drawings.find((drawing) => drawing.id === drawingId);
    const drawingName = currentDrawing?.objectName!;

    setConfirmDialogOptions({ drawingId, drawingName });
  };

  const handleTabChange = useCallback(
    (newTab: number): void => {
      if (newTab !== selectedTab) {
        deleteGeoJSONLayer();
        deleteHoverPreviewLayer();
        dispatch(
          warningsDrawingsActions.setDrawValues({
            objectName: '',
            drawingInstanceId: drawingDialogType,
            id: '',
          }),
        );
      }
      setSelectedTab(newTab);
    },
    [selectedTab, deleteGeoJSONLayer, deleteHoverPreviewLayer, dispatch],
  );

  return (
    <>
      <AreaDrawingManager
        activeDrawingId={activeDrawingId}
        bounds={bounds}
        drawingListItems={drawings}
        error={uiError}
        headerSize="xs"
        isLoading={
          isFetchingDetails || isFetchingList || isFetchingPredefinedAreasList
        }
        isOpen={isDialogOpen}
        onChangeTab={handleTabChange}
        onClickArea={onClickArea}
        onClickDrawing={onClickDrawing}
        onClose={onClose}
        onCreateNewDrawing={onCreateNewDrawing}
        onDeleteDrawing={onDeleteDrawing}
        onEditDrawing={onEditDrawing}
        onHoverArea={onHover}
        onHoverAreaEnd={onHoverEnd}
        onHoverDrawing={onHover}
        onHoverDrawingEnd={onHoverEnd}
        onHoverDrawingFeature={onHoverDrawingFeature}
        onHoverDrawingFeatureEnd={onHoverDrawingFeatureEnd}
        onMouseDown={setDialogOrder}
        onShareDrawing={shareDrawing}
        order={dialogOrder}
        predefinedAreaListItems={predefinedAreas}
        selectedAreaFeatureNamesByAreaId={selectedAreaFeatureNamesByAreaId}
        selectedTab={selectedTab}
        source={uiSource}
        startPosition={{ top: 104, left: 50 }}
        title={`${t('area-drawing-manager-title')} ${mapId}`}
      />
      {!!confirmDialogOptions && (
        <ConfirmDeleteDialog
          {...confirmDialogOptions}
          onClose={(): void => setConfirmDialogOptions(undefined)}
          onDeleteSucces={onDeleteSuccess}
        />
      )}
    </>
  );
};
